import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
}

export default function CookieConsent() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem('legalvio-cookie-consent');
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (prefs: CookiePreferences) => {
    localStorage.setItem('legalvio-cookie-consent', JSON.stringify(prefs));
    localStorage.setItem('legalvio-cookie-timestamp', Date.now().toString());
    setVisible(false);
    setShowCustomize(false);
  };

  const handleAcceptAll = () => {
    saveConsent({ essential: true, analytics: true, marketing: true });
  };

  const handleEssentialOnly = () => {
    saveConsent({ essential: true, analytics: false, marketing: false });
  };

  const togglePreference = (key: keyof CookiePreferences) => {
    if (key === 'essential') return;
    setPreferences((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (!visible) return null;

  return (
    <>
      <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm transition-opacity duration-300" onClick={handleEssentialOnly}></div>

      {!showCustomize ? (
        <div className="fixed bottom-0 left-0 right-0 z-[101] animate-slide-up">
          <div className="bg-background-50 border-t border-background-200/70 shadow-lg">
            <div className="w-full px-6 md:px-10 lg:px-16 py-5">
              <div className="max-w-[1400px] mx-auto flex flex-col sm:flex-row items-start sm:items-center gap-5">
                <div className="flex-1">
                  <h3 className="font-heading text-sm font-semibold text-foreground-950 mb-1">
                    {t('cookie.title')}
                  </h3>
                  <p className="text-xs text-foreground-500 leading-relaxed max-w-2xl">
                    {t('cookie.description')}
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <button
                    onClick={() => setShowCustomize(true)}
                    className="px-4 py-2.5 text-xs font-semibold text-foreground-600 border border-background-300 rounded-full hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {t('cookie.customize')}
                  </button>
                  <button
                    onClick={handleEssentialOnly}
                    className="px-4 py-2.5 text-xs font-semibold text-foreground-600 border border-background-300 rounded-full hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {t('cookie.essentialOnly')}
                  </button>
                  <button
                    onClick={handleAcceptAll}
                    className="px-6 py-2.5 text-xs font-semibold text-white bg-primary-500 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {t('cookie.acceptAll')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="fixed inset-0 z-[101] flex items-center justify-center p-4">
          <div className="bg-background-50 rounded-2xl border border-background-200/70 shadow-lg w-full max-w-lg p-6 animate-scale-in">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading text-lg font-semibold text-foreground-950">
                {t('cookie.title')}
              </h3>
              <button
                onClick={() => setShowCustomize(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>

            <div className="flex flex-col gap-4 mb-6">
              <label className="flex items-start gap-3 p-4 rounded-xl bg-background-100/50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.essential}
                  disabled
                  className="mt-0.5 accent-primary-500"
                />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-foreground-950 mb-1">
                    {t('cookie.essential')}
                  </div>
                  <div className="text-xs text-foreground-500 leading-relaxed">
                    {t('cookie.essentialDesc')}
                  </div>
                </div>
              </label>

              <label className="flex items-start gap-3 p-4 rounded-xl bg-background-100/50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.analytics}
                  onChange={() => togglePreference('analytics')}
                  className="mt-0.5 accent-primary-500"
                />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-foreground-950 mb-1">
                    {t('cookie.analytics')}
                  </div>
                  <div className="text-xs text-foreground-500 leading-relaxed">
                    {t('cookie.analyticsDesc')}
                  </div>
                </div>
              </label>

              <label className="flex items-start gap-3 p-4 rounded-xl bg-background-100/50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.marketing}
                  onChange={() => togglePreference('marketing')}
                  className="mt-0.5 accent-primary-500"
                />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-foreground-950 mb-1">
                    {t('cookie.marketing')}
                  </div>
                  <div className="text-xs text-foreground-500 leading-relaxed">
                    {t('cookie.marketingDesc')}
                  </div>
                </div>
              </label>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => saveConsent(preferences)}
                className="flex-1 px-5 py-3 text-sm font-semibold text-white bg-primary-500 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                {t('cookie.savePreferences')}
              </button>
              <button
                onClick={handleAcceptAll}
                className="flex-1 px-5 py-3 text-sm font-semibold text-primary-600 border border-primary-200 rounded-full hover:bg-primary-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                {t('cookie.acceptAll')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}